import React, { useEffect, useMemo } from 'react';
import { required } from '~/app/helpers/validation';
import Loader from '~/app/common/Loader';
import useForm, { UseFormOptionFields } from '~/app/modules/form/useForm';
import { IssueClosePayload, IssueDetails } from '~/types';
import classes from './style.module.scss';
import { IssueCloseTypes } from '~/app/data/statuses/issue';
import useDidMountEffect from '~/app/hooks/useDidMountEffect';
import Details from './Details';
import Footer from './Footer';
import { useStoreActions, useStoreState } from '~/store';

export type FormProps = {
  onClose(): void
  issueDetails: IssueDetails
  onSave?: (id: IssueDetails['id']) => void
}

export type FormComponent = React.FC<FormProps>

const Form: FormComponent = ({ onClose, issueDetails, onSave }) => {
  const machine = useStoreState(state => state.machine.data);

  const displayTypeInIssues = useStoreState(state => state.machine.displayTypeInIssues);
  const isIssueTypeRequired = useStoreState(state => state.machine.isIssueTypeRequired);
  const isHPCodeRequired = useStoreState(state => state.issue.isHPCodeRequired);
  const hasSimplifiedWorkorderEnabled = useStoreState(state => state.issue.data?.hasSimplifiedWorkorderEnabled ?? false);
  const isWorkOrderFormActive = useStoreState(state => state.issue.isWorkOrderFormActive);
  const displayCustomer = useStoreState(state => state.issue.displayCustomer);
  const displayProject = useStoreState(state => state.issue.displayProject);

  const formFields = useMemo(() => {
    const fields: UseFormOptionFields = [
      ['issueCloseType', { validators: [['required', required]] }],
      ['comment', { validators: [['required', required]] }],
    ];

    if (hasSimplifiedWorkorderEnabled) {
      !!machine.isOperatingTimeVisible && fields.push(['operatingTime', isWorkOrderFormActive && !!machine.operatingTimeAllowed ? { validators: [['required', required]] } : {}]);
      !!machine.isOperatingCountVisible && fields.push(['operatingCount', isWorkOrderFormActive && !!machine.operatingCountAllowed ? { validators: [['required', required]] } : {}]);
      displayTypeInIssues && fields.push(['sortingType', isWorkOrderFormActive && isIssueTypeRequired ? { validators: [['required', required]] } : {}]);
      isHPCodeRequired && fields.push(['hpCode', isWorkOrderFormActive ? { validators: [['required', required]] } : {}]);
      fields.push(['ordinaryHours', isWorkOrderFormActive ? { validators: [['required', required]] } : {}]);
      fields.push(['overTime50', {}]);
      fields.push(['overTime100', {}]);
      fields.push(['workType', isWorkOrderFormActive ? { validators: [['required', required]] } : {}]);
      fields.push(['costCategory', isWorkOrderFormActive ? { validators: [['required', required]] } : {}]);
      fields.push(['customer', isWorkOrderFormActive && displayCustomer ? { validators: [['required', required]] } : {}]);
      fields.push(['project', isWorkOrderFormActive && displayProject ? { validators: [['required', required]] } : {}]);
    }

    return fields;
  }, [displayCustomer, displayProject, displayTypeInIssues, hasSimplifiedWorkorderEnabled, isHPCodeRequired, isIssueTypeRequired, isWorkOrderFormActive, machine.isOperatingCountVisible, machine.isOperatingTimeVisible, machine.operatingCountAllowed, machine.operatingTimeAllowed]);

  const storeUpdateForm = useStoreActions(actions => actions.form.updateForm);

  useEffect(() => {
    if (hasSimplifiedWorkorderEnabled && !isWorkOrderFormActive) {
      const resetState = { validators: [], errors: [], state: { touched: false, invalid: false } };
      storeUpdateForm({
        formName: 'closeIssue',
        fields: {
          ...(machine.isOperatingTimeVisible && { operatingTime: resetState }),
          ...(machine.isOperatingCountVisible && { operatingCount: resetState }),
          ...(displayTypeInIssues && { sortingType: resetState }),
          ...(isHPCodeRequired && { hpCode: resetState }),
          ordinaryHours: resetState,
          overTime50: resetState,
          overTime100: resetState,
          workType: resetState,
          costCategory: resetState,
          ...(displayCustomer && { customer: resetState }),
          ...(displayProject && { project: resetState })
        }
      });
    }
  }, [hasSimplifiedWorkorderEnabled, isWorkOrderFormActive, storeUpdateForm, machine.isOperatingTimeVisible, machine.isOperatingCountVisible, displayTypeInIssues, isHPCodeRequired, displayCustomer, displayProject]);

  const { values, defaultValues, removeForm } = useForm<IssueClosePayload>({
    formName: 'closeIssue',
    fields: formFields,
    defaultValues: {
      issueCloseType: IssueCloseTypes.CORRECTED,
      comment: '',
      ...hasSimplifiedWorkorderEnabled && {
        ...machine.isOperatingTimeVisible && {operatingTime: 0},
        ...machine.isOperatingCountVisible && {operatingCount: 0},
        sortingType: displayTypeInIssues && issueDetails?.sortingType ? Number(issueDetails?.sortingType) : 0,
        hpCode: 0,
        workType: 0,
        costCategory: 0,
        customer: 0,
        project: 0,
        ordinaryHours: undefined,
        overTime50: undefined,
        overTime100: undefined
      },
    }
  });

  useDidMountEffect(() => (() => removeForm()));


  if (!values) {
    return <Loader size="32" />;
  }

  return (
    <>
      <div className={classes['container']}>
        <Details />
      </div>

      <Footer onClose={onClose} issueDetails={issueDetails} defaultFormValues={defaultValues} onSave={onSave} />
    </>
  );
};

export default Form;
